/**
 * Arthur Dent Insurance — home-insurance application.
 *
 * One application form with every question on it, two ways to complete it:
 *
 *   Path 1  Fill it in yourself. All fields are shown, with a live count of how
 *           many there are and how many are complete.
 *   Path 2  Complete with Squiryl. The real <squiryl-button> shares the details
 *           your vault holds; the same form then fills in on screen for review,
 *           leaving only the questions a vault cannot answer.
 *
 * The squiryl:share/declined/expired/error/statechange event contract is the
 * SDK's and is left untouched.
 */
import '@squiryl/button';

const $ = (id) => document.getElementById(id);

/** Escape untrusted values before they touch innerHTML — a shared value comes
 *  from the buyer's vault, so a field could contain HTML/script. */
const esc = (s) => String(s).replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const gbp = (n) => `£${Number(n).toLocaleString('en-GB')}`;

// ── The application: every field ──────────────────────────────────────────
//
// `id` is `section.field` for fields a Squiryl vault can answer (the section and
// field names are the package's, so a shared payload drops straight in) and
// `cover.*` / `history.*` / `declaration.*` for the questions only the insurer asks.

const YES_NO = [['true', 'Yes'], ['false', 'No']];
const COUNTRIES = [['GB', 'United Kingdom'], ['IE', 'Ireland'], ['JE', 'Jersey'], ['GG', 'Guernsey'], ['IM', 'Isle of Man']];

const FIELDS = [
  // ─ About you ─
  { section: 'About you', id: 'identity.title', label: 'Title', type: 'select', options: [['mr', 'Mr'], ['mrs', 'Mrs'], ['miss', 'Miss'], ['ms', 'Ms'], ['mx', 'Mx'], ['dr', 'Dr'], ['other', 'Other']] },
  { section: 'About you', id: 'identity.first_name', label: 'First name', type: 'text', required: true },
  { section: 'About you', id: 'identity.middle_names', label: 'Middle names', type: 'text' },
  { section: 'About you', id: 'identity.last_name', label: 'Last name', type: 'text', required: true },
  { section: 'About you', id: 'identity.date_of_birth', label: 'Date of birth', type: 'date', required: true },
  { section: 'About you', id: 'identity.marital_status', label: 'Marital status', type: 'select', options: [['single', 'Single'], ['married', 'Married'], ['civil_partnership', 'Civil partnership'], ['divorced', 'Divorced'], ['widowed', 'Widowed'], ['separated', 'Separated']] },
  { section: 'About you', id: 'occupations.job_title', label: 'Job title', type: 'text' },
  { section: 'About you', id: 'occupations.employment_type', label: 'Employment', type: 'select', options: [['full_time', 'Full time'], ['part_time', 'Part time'], ['self_employed', 'Self-employed'], ['contract', 'Contract'], ['retired', 'Retired'], ['student', 'Student'], ['other', 'Other']] },

  // ─ Contact ─
  { section: 'Contact', id: 'contact_emails.email', label: 'Email', type: 'email', required: true },
  { section: 'Contact', id: 'contact_phones.phone_number', label: 'Phone', type: 'tel', required: true },
  { section: 'Contact', id: 'addresses.line_1', label: 'Address line 1', type: 'text', required: true },
  { section: 'Contact', id: 'addresses.line_2', label: 'Address line 2', type: 'text' },
  { section: 'Contact', id: 'addresses.city', label: 'Town or city', type: 'text', required: true },
  { section: 'Contact', id: 'addresses.county', label: 'County', type: 'text' },
  { section: 'Contact', id: 'addresses.province_code', label: 'Region code (for example GB-ENG)', type: 'text' },
  { section: 'Contact', id: 'addresses.postcode', label: 'Postcode', type: 'text', required: true },
  { section: 'Contact', id: 'addresses.country', label: 'Country', type: 'select', options: COUNTRIES, required: true },

  // ─ The property ─
  { section: 'The property', id: 'property.property_type_id', label: 'Property type', type: 'select', options: [['House', 'House'], ['Flat', 'Flat'], ['Bungalow', 'Bungalow'], ['Maisonette', 'Maisonette'], ['Other', 'Other']], required: true },
  { section: 'The property', id: 'property.ownership_status', label: 'Ownership', type: 'select', options: [['owned_outright', 'Owned outright'], ['mortgaged', 'Mortgaged'], ['renting', 'Renting'], ['living_with_family', 'Living with family'], ['other', 'Other']], required: true },
  { section: 'The property', id: 'property.tenure', label: 'Tenure', type: 'select', options: [['freehold', 'Freehold'], ['leasehold', 'Leasehold'], ['shared_ownership', 'Shared ownership'], ['commonhold', 'Commonhold'], ['other', 'Other']] },
  { section: 'The property', id: 'property.is_primary_residence', label: 'Is this your main home?', type: 'select', options: YES_NO, required: true },
  { section: 'The property', id: 'property.purchase_date', label: 'Date you bought it', type: 'date' },
  { section: 'The property', id: 'property.purchase_price', label: 'Purchase price (£)', type: 'number', min: 0 },
  { section: 'The property', id: 'property.current_estimated_value', label: 'Estimated value today (£)', type: 'number', min: 0, required: true },
  { section: 'The property', id: 'property.bedrooms', label: 'Bedrooms', type: 'number', min: 0, max: 30, required: true },
  { section: 'The property', id: 'property.bathrooms', label: 'Bathrooms', type: 'number', min: 0, max: 30 },
  { section: 'The property', id: 'property.reception_rooms', label: 'Reception rooms', type: 'number', min: 0, max: 30 },

  // ─ How it is built ─
  { section: 'How it is built', id: 'property.year_built', label: 'Year built', type: 'number', min: 1500, max: 2100, required: true },
  { section: 'How it is built', id: 'property.wall_construction_id', label: 'Walls', type: 'select', options: [['Brick', 'Brick'], ['Stone', 'Stone'], ['Timber frame', 'Timber frame'], ['Concrete', 'Concrete'], ['Cob or mud', 'Cob or mud'], ['Other', 'Other']], required: true },
  { section: 'How it is built', id: 'property.roof_construction_id', label: 'Roof', type: 'select', options: [['Slate or tile', 'Slate or tile'], ['Flat roof', 'Flat roof'], ['Thatch', 'Thatch'], ['Metal', 'Metal'], ['Other', 'Other']], required: true },
  { section: 'How it is built', id: 'property.flat_roof_percent', label: 'Flat roof (% of the roof)', type: 'number', min: 0, max: 100 },
  { section: 'How it is built', id: 'property.listing_grade', label: 'Listed building', type: 'select', options: [['not_listed', 'Not listed'], ['grade_I', 'Grade I'], ['grade_II*', 'Grade II*'], ['grade_II', 'Grade II'], ['locally_listed', 'Locally listed']], required: true },
  { section: 'How it is built', id: 'property.storeys', label: 'Storeys in the building', type: 'number', min: 1, max: 60, required: true },
  { section: 'How it is built', id: 'property.flat_floor', label: 'Floor of the flat', type: 'number', min: 0, max: 60, showIf: (v) => v['property.property_type_id'] === 'Flat' },
  { section: 'How it is built', id: 'property.has_been_extended', label: 'Has the property been extended?', type: 'select', options: YES_NO, required: true },
  { section: 'How it is built', id: 'property.year_extended', label: 'Year extended', type: 'number', min: 1500, max: 2100, showIf: (v) => v['property.has_been_extended'] === 'true' },

  // ─ History and security ─
  { section: 'History and security', id: 'property.ever_flooded', label: 'Has the property ever flooded?', type: 'select', options: YES_NO, required: true },
  { section: 'History and security', id: 'property.has_subsidence', label: 'Subsidence, heave or landslip?', type: 'select', options: YES_NO, required: true },
  { section: 'History and security', id: 'property.underpinned', label: 'Has it been underpinned?', type: 'select', options: YES_NO, showIf: (v) => v['property.has_subsidence'] === 'true' },
  { section: 'History and security', id: 'property.door_lock_id', label: 'External door lock', type: 'select', options: [['5-lever mortice deadlock', '5-lever mortice deadlock'], ['Multi-point lock', 'Multi-point lock'], ['Standard latch', 'Standard latch'], ['Other', 'Other']], required: true },
  { section: 'History and security', id: 'property.window_locks_fitted', label: 'Window locks fitted?', type: 'select', options: YES_NO, required: true },
  { section: 'History and security', id: 'property.alarm_type_id', label: 'Burglar alarm', type: 'select', options: [['No alarm', 'No alarm'], ['Audible alarm', 'Audible alarm'], ['Monitored alarm', 'Monitored alarm']] },
  { section: 'History and security', id: 'property.smoke_alarms_fitted', label: 'Smoke alarms fitted?', type: 'select', options: YES_NO, required: true },

  // ─ Your cover ─
  { section: 'Your cover', id: 'cover.cover_type', label: 'What do you want to cover?', type: 'select', options: [['buildings_contents', 'Buildings and contents'], ['buildings', 'Buildings only'], ['contents', 'Contents only']], required: true },
  { section: 'Your cover', id: 'cover.start_date', label: 'Cover start date', type: 'date', required: true },
  { section: 'Your cover', id: 'cover.buildings_sum', label: 'Buildings rebuild cost (£)', type: 'number', min: 0, required: true, showIf: (v) => v['cover.cover_type'] !== 'contents' },
  { section: 'Your cover', id: 'cover.contents_sum', label: 'Contents value (£)', type: 'number', min: 0, required: true, showIf: (v) => v['cover.cover_type'] !== 'buildings' },
  { section: 'Your cover', id: 'cover.accidental_damage', label: 'Accidental damage', type: 'select', options: [['none', 'Not included'], ['buildings', 'Buildings'], ['contents', 'Contents'], ['both', 'Buildings and contents']], required: true },
  { section: 'Your cover', id: 'cover.possessions_away', label: 'Personal possessions away from home', type: 'select', options: [['0', 'Not covered'], ['1500', 'Up to £1,500'], ['3000', 'Up to £3,000'], ['5000', 'Up to £5,000']], required: true },
  { section: 'Your cover', id: 'cover.excess', label: 'Voluntary excess', type: 'select', options: [['100', '£100'], ['250', '£250'], ['500', '£500'], ['1000', '£1,000']], required: true },
  { section: 'Your cover', id: 'cover.legal_expenses', label: 'Add legal expenses cover?', type: 'select', options: YES_NO },
  { section: 'Your cover', id: 'cover.home_emergency', label: 'Add home emergency cover?', type: 'select', options: YES_NO },

  // ─ Who lives there and how it is used ─
  { section: 'Who lives there', id: 'cover.adults', label: 'Adults living in the home', type: 'number', min: 1, max: 20, required: true },
  { section: 'Who lives there', id: 'cover.children', label: 'Children under 18', type: 'number', min: 0, max: 20, required: true },
  { section: 'Who lives there', id: 'cover.occupancy', label: 'How is the home used?', type: 'select', options: [['owner_occupied', 'I live there (main home)'], ['second_home', 'Second home'], ['let_to_tenants', 'Let to tenants'], ['holiday_let', 'Holiday let']], required: true },
  { section: 'Who lives there', id: 'cover.unoccupied', label: 'Longest the home is left empty in a year', type: 'select', options: [['under_30', 'Under 30 days'], ['30_to_60', '30 to 60 days'], ['over_60', 'Over 60 days']], required: true },
  { section: 'Who lives there', id: 'cover.lodgers', label: 'Does a lodger or paying guest live there?', type: 'select', options: YES_NO, required: true },
  { section: 'Who lives there', id: 'cover.work_from_home', label: 'Do you work from the home?', type: 'select', options: [['no', 'No'], ['sometimes', 'Sometimes, office-type work'], ['full', 'Yes, mainly from home']], required: true },
  { section: 'Who lives there', id: 'cover.business_visitors', label: 'Do clients or customers visit the home?', type: 'select', options: YES_NO, required: true },
  { section: 'Who lives there', id: 'cover.business_equipment', label: 'Business equipment kept at home (£)', type: 'number', min: 0 },

  // ─ Valuables and current cover ─
  { section: 'Valuables and current cover', id: 'valuables.description', label: 'Most valuable single item', type: 'text' },
  { section: 'Valuables and current cover', id: 'valuables.valuable_taxonomy_id', label: 'What type of item', type: 'select', options: [['Watch', 'Watch'], ['Jewellery', 'Jewellery'], ['Art', 'Art'], ['Electronics', 'Electronics'], ['Musical instrument', 'Musical instrument'], ['Other', 'Other']] },
  { section: 'Valuables and current cover', id: 'valuables.current_valuation', label: 'Its value (£)', type: 'number', min: 0 },
  { section: 'Valuables and current cover', id: 'insurance.policy_type', label: 'Current policy type', type: 'select', options: [['home_combined', 'Buildings and contents'], ['buildings', 'Buildings'], ['home_contents', 'Contents'], ['none', 'No current policy']] },
  { section: 'Valuables and current cover', id: 'insurance.insurer_name', label: 'Current insurer', type: 'text' },
  { section: 'Valuables and current cover', id: 'insurance.policy_end_date', label: 'Current policy renews', type: 'date' },
  { section: 'Valuables and current cover', id: 'insurance.premium_amount', label: 'Current premium (£ a month)', type: 'number', min: 0 },

  // ─ Your history ─
  { section: 'Your history', id: 'history.claims', label: 'Claims in the last 5 years (any insurer)', type: 'select', options: [['0', 'None'], ['1', '1'], ['2', '2'], ['3', '3 or more']], required: true },
  { section: 'Your history', id: 'history.claims_detail', label: 'Briefly, what happened and what was claimed?', type: 'textarea', required: true, showIf: (v) => Number(v['history.claims']) > 0 },
  { section: 'Your history', id: 'history.ncd_years', label: 'Years without a claim', type: 'select', options: [['0', 'None'], ['1', '1'], ['2', '2'], ['3', '3'], ['4', '4'], ['5', '5 or more']], required: true },
  { section: 'Your history', id: 'history.refused', label: 'Has an insurer refused, cancelled or applied special terms to your home insurance?', type: 'select', options: YES_NO, required: true },
  { section: 'Your history', id: 'history.ccj', label: 'County court judgment, bankruptcy or IVA in the last 6 years?', type: 'select', options: YES_NO, required: true },
  { section: 'Your history', id: 'history.convictions', label: 'Any unspent criminal conviction?', type: 'select', options: YES_NO, required: true },

  // ─ Declaration ─
  { section: 'Declaration', id: 'declaration.confirmed', label: 'I confirm my answers are correct and complete to the best of my knowledge, and that false or incomplete answers can void my policy.', type: 'checkbox', required: true },
  { section: 'Declaration', id: 'declaration.marketing', label: 'Email me about Arthur Dent Insurance products and offers', type: 'checkbox' },
];

const VAULT_SECTIONS = new Set(['identity', 'addresses', 'contact_emails', 'contact_phones', 'occupations', 'property', 'valuables', 'insurance']);
const isVaultField = (id) => VAULT_SECTIONS.has(id.split('.')[0]);

/** Current answers, keyed by field id. */
const values = {};
/** Field ids whose value came from Squiryl (shown read-only). */
const fromSquiryl = new Set();

const isVisible = (f) => !f.showIf || f.showIf(values);
const isFilled = (f) => {
  const v = values[f.id];
  if (f.type === 'checkbox') return v === 'true';
  return v != null && String(v).trim() !== '';
};

// ── Rendering ─────────────────────────────────────────────────────────────

const sectionsEl = $('form-sections');
const inputId = (f) => `f-${f.id.replace(/\./g, '-')}`;

function controlHtml(f) {
  const id = inputId(f);
  const readonly = fromSquiryl.has(f.id);
  const value = values[f.id] ?? '';
  const common = `id="${id}" name="${esc(f.id)}" data-field="${esc(f.id)}"${readonly ? ' disabled' : ''}`;
  if (f.type === 'select') {
    const known = f.options.some(([v]) => v === value);
    const options = [...f.options, ...(value !== '' && !known ? [[value, value]] : [])];
    return `<select ${common}><option value="">Select…</option>${options
      .map(([v, l]) => `<option value="${esc(v)}"${v === value ? ' selected' : ''}>${esc(l)}</option>`).join('')}</select>`;
  }
  if (f.type === 'textarea') return `<textarea ${common} rows="3" maxlength="400">${esc(value)}</textarea>`;
  if (f.type === 'checkbox') return `<input type="checkbox" ${common}${value === 'true' ? ' checked' : ''} />`;
  const attrs = [f.min != null ? `min="${f.min}"` : '', f.max != null ? `max="${f.max}"` : ''].filter(Boolean).join(' ');
  return `<input type="${f.type}" ${common} value="${esc(value)}" ${attrs} />`;
}

function renderForm() {
  const bySection = new Map();
  for (const f of FIELDS) {
    if (!bySection.has(f.section)) bySection.set(f.section, []);
    bySection.get(f.section).push(f);
  }
  sectionsEl.innerHTML = [...bySection.entries()].map(([section, fields]) => `
    <fieldset data-section="${esc(section)}">
      <legend>${esc(section)} <span class="section-count" data-count-for="${esc(section)}"></span></legend>
      ${fields.map((f) => {
        const tag = fromSquiryl.has(f.id) ? '<span class="from-squiryl">From Squiryl</span>' : '';
        const req = f.required ? '<span class="req" aria-label="required">*</span>' : '';
        if (f.type === 'checkbox') {
          return `<div class="field field-check" data-wrap="${esc(f.id)}"><label>${controlHtml(f)} <span>${esc(f.label)}${req}</span></label></div>`;
        }
        return `<div class="field" data-wrap="${esc(f.id)}"><label for="${inputId(f)}">${esc(f.label)}${req}${tag}</label>${controlHtml(f)}</div>`;
      }).join('')}
    </fieldset>`).join('');
  updateVisibilityAndCounts();
}

/** Show or hide conditional fields and refresh every counter. */
function updateVisibilityAndCounts() {
  let total = 0; let done = 0; let reqTotal = 0; let reqDone = 0; let shared = 0;
  const perSection = new Map();
  for (const f of FIELDS) {
    const wrap = sectionsEl.querySelector(`[data-wrap="${CSS.escape(f.id)}"]`);
    const visible = isVisible(f);
    if (wrap) wrap.hidden = !visible;
    if (!visible) continue;
    total += 1;
    const filled = isFilled(f);
    if (filled) done += 1;
    if (filled && fromSquiryl.has(f.id)) shared += 1;
    if (f.required) { reqTotal += 1; if (filled) reqDone += 1; }
    const s = perSection.get(f.section) ?? { done: 0, total: 0 };
    s.total += 1; if (filled) s.done += 1;
    perSection.set(f.section, s);
  }
  for (const [section, s] of perSection) {
    const el = sectionsEl.querySelector(`[data-count-for="${CSS.escape(section)}"]`);
    if (el) el.textContent = `${s.done}/${s.total}`;
  }
  const pct = total ? Math.round((done / total) * 100) : 0;
  $('completion-count').textContent = `${done} of ${total} fields complete`;
  $('completion-percent').textContent = `${pct}%`;
  $('completion-fill').style.width = `${pct}%`;
  $('completion-required').textContent = `${reqDone} of ${reqTotal} required fields complete`;
  $('completion-source').textContent = shared ? `${shared} filled from Squiryl` : '';
  const ready = reqDone === reqTotal;
  $('get-quote-btn').disabled = !ready;
  $('form-hint').textContent = ready
    ? 'All required fields are complete.'
    : `${reqTotal - reqDone} required ${reqTotal - reqDone === 1 ? 'field' : 'fields'} still to complete.`;
}

function onInput(e) {
  const el = e.target.closest('[data-field]');
  if (!el) return;
  values[el.dataset.field] = el.type === 'checkbox' ? String(el.checked) : el.value;
  updateVisibilityAndCounts();
}
sectionsEl.addEventListener('input', onInput);
sectionsEl.addEventListener('change', onInput);

// ── Navigation ────────────────────────────────────────────────────────────

const steps = { choose: $('step-choose'), squiryl: $('step-squiryl'), form: $('step-form') };
const progressEls = Array.from(document.querySelectorAll('.quote-progress .step'));
let path = null;

function show(name, progressStep) {
  for (const [key, el] of Object.entries(steps)) el.hidden = key !== name;
  for (const el of progressEls) {
    const n = Number(el.dataset.step);
    el.classList.toggle('active', n === progressStep);
    el.classList.toggle('done', n < progressStep);
  }
  window.scrollTo({ top: 0 });
}

function setLabels(l1, l2, l3) {
  $('step-label-1').textContent = l1; $('step-label-2').textContent = l2; $('step-label-3').textContent = l3;
}

function resetAnswers() {
  for (const k of Object.keys(values)) delete values[k];
  fromSquiryl.clear();
  values['cover.start_date'] = new Date().toISOString().slice(0, 10);
}

$('path-form').addEventListener('click', () => {
  path = 'form';
  resetAnswers();
  setLabels('How to apply', 'Application', 'Your quote');
  $('form-title').textContent = 'Your application';
  $('source-banner').hidden = true;
  renderForm();
  show('form', 2);
});

$('path-squiryl').addEventListener('click', () => {
  path = 'squiryl';
  resetAnswers();
  setLabels('How to apply', 'Scan with Squiryl', 'Review & quote');
  show('squiryl', 2);
});

$('back-from-squiryl').addEventListener('click', () => show('choose', 1));
$('back-from-form').addEventListener('click', () => { setLabels('How to apply', 'Application', 'Your quote'); show('choose', 1); });

// ── Squiryl: event contract is the SDK's — do not change ──────────────────

const btn = document.querySelector('squiryl-button');
const statusEl = $('status');
const shareErrorEl = $('share-error');
let sharedKycVerified = false;

btn.addEventListener('squiryl:statechange', (e) => { statusEl.textContent = `state: ${e.detail.state}`; });

function greetingName(payload) {
  const id = payload?.identity ?? {};
  return id.preferred_name || [id.first_name, id.last_name].filter(Boolean).join(' ') || 'there';
}

/** Drop the shared payload into the application and mark those fields as shared. */
function populateFromPayload(payload) {
  for (const f of FIELDS) {
    if (!isVaultField(f.id)) continue;
    const [section, field] = f.id.split('.');
    const v = payload?.[section]?.[field];
    if (v != null && String(v) !== '') {
      values[f.id] = String(v);
      fromSquiryl.add(f.id);
    }
  }
}

/**
 * DEMO ONLY. Answers the vault could hold in future (household, usage, claims
 * history) but does not yet. The Squiryl path pretends they were shared, so the
 * review screen shows them filled "From Squiryl". The mobile app does not list
 * them in the request. Only used when the real payload did not supply the field.
 */
const DEMO_FUTURE_SHARED = {
  'property.year_extended': '2012',
  'property.underpinned': 'true',
  'cover.adults': '2',
  'cover.children': '0',
  'cover.occupancy': 'owner_occupied',
  'cover.unoccupied': 'under_30',
  'cover.lodgers': 'false',
  'cover.work_from_home': 'sometimes',
  'cover.business_visitors': 'false',
  'cover.business_equipment': '800',
  'history.claims': '0',
  'history.ncd_years': '5',
  'history.refused': 'false',
  'history.ccj': 'false',
  'history.convictions': 'false',
};

function applyDemoFutureShared() {
  for (const [id, val] of Object.entries(DEMO_FUTURE_SHARED)) {
    if (values[id] == null || values[id] === '') {
      values[id] = val;
      fromSquiryl.add(id);
    }
  }
}

btn.addEventListener('squiryl:share', (e) => {
  const { payload, kyc_verified } = e.detail;

  // K-delivery (the webhook) is async and can fail independently of the session
  // completing. If it did, `payload` is null — say so instead of printing "null".
  if (!payload || Object.keys(payload).length === 0) {
    shareErrorEl.hidden = false;
    shareErrorEl.innerHTML =
      '<p><strong>The consent went through, but the encrypted data window never reached this server.</strong></p>' +
      '<p>That usually means the Squiryl <em>webhook</em> couldn\u2019t deliver the contract key — the webhook URL ' +
      'registered in the developer portal isn\u2019t publicly reachable. Expose this server with a tunnel and set ' +
      'that exact URL as your webhook, then try again.</p>';
    return;
  }

  sharedKycVerified = !!kyc_verified;
  populateFromPayload(payload);
  applyDemoFutureShared();
  statusEl.innerHTML = `Thanks, <strong>${esc(greetingName(payload))}</strong>!`;
  $('form-title').textContent = 'Review your application';
  const banner = $('source-banner');
  banner.hidden = false;
  banner.innerHTML = `${sharedKycVerified ? '&#10003; Verified human. ' : ''}<strong>${esc(greetingName(payload))}</strong>, ` +
    `your Squiryl vault filled in ${fromSquiryl.size} fields. Check them, then choose your cover and confirm the declaration.`;
  renderForm();
  show('form', 3);
});

btn.addEventListener('squiryl:declined', () => { statusEl.textContent = 'You declined the share.'; });
btn.addEventListener('squiryl:expired', () => { statusEl.textContent = 'The request expired — tap again.'; });
btn.addEventListener('squiryl:error', (e) => { statusEl.textContent = `error: ${e.detail.error}`; });

// ── Turn the answers into a payload and cover details, then price ─────────

const is = (value) => String(value) === 'true';

function buildPayload() {
  const payload = {};
  for (const f of FIELDS) {
    if (!isVaultField(f.id) || !isVisible(f) || !isFilled(f)) continue;
    const [section, field] = f.id.split('.');
    (payload[section] ??= {})[field] = values[f.id];
  }
  return payload;
}

function buildCover() {
  const v = (id) => values[id];
  const num = (id) => Number(v(id)) || 0;
  const type = v('cover.cover_type');
  return {
    product: 'home',
    coverType: type,
    startDate: v('cover.start_date'),
    buildingsSum: type === 'contents' ? 0 : num('cover.buildings_sum'),
    contentsSum: type === 'buildings' ? 0 : num('cover.contents_sum'),
    accidentalDamage: v('cover.accidental_damage'),
    possessionsAway: num('cover.possessions_away'),
    excess: num('cover.excess'),
    legalExpenses: is(v('cover.legal_expenses')),
    homeEmergency: is(v('cover.home_emergency')),
    adults: num('cover.adults'),
    children: num('cover.children'),
    occupancy: v('cover.occupancy'),
    unoccupied: v('cover.unoccupied'),
    lodgers: is(v('cover.lodgers')),
    workFromHome: v('cover.work_from_home'),
    businessVisitors: is(v('cover.business_visitors')),
    businessEquipment: num('cover.business_equipment'),
    claims: num('history.claims'),
    claimsDetail: (v('history.claims_detail') ?? '').trim(),
    ncdYears: num('history.ncd_years'),
    refused: is(v('history.refused')),
    ccj: is(v('history.ccj')),
    convictions: is(v('history.convictions')),
    marketing: is(v('declaration.marketing')),
  };
}

/**
 * A fake, deterministic annual premium built from the cover chosen and the
 * facts on the application. Demo flavour only — but every factor is real
 * underwriting, so each answer visibly moves the price.
 * Returns the price, a line-by-line breakdown, and any reasons the quote is
 * referred to an underwriter instead of priced online.
 */
function priceQuote(cover, payload) {
  const home = payload.property ?? {};
  const lines = [];
  const referrals = [];
  let buildings = cover.buildingsSum * 0.0011;
  let contents = cover.contentsSum * 0.0034;

  // Construction and age
  const built = Number(home.year_built);
  if (built && built < 1900) buildings *= 1.25;
  else if (built && built < 1945) buildings *= 1.1;
  if (/thatch|timber|wood|cob/i.test(`${home.wall_construction_id ?? ''} ${home.roof_construction_id ?? ''}`)) buildings *= 1.4;
  if (Number(home.flat_roof_percent) > 25) buildings *= 1.15;
  if (home.listing_grade && home.listing_grade !== 'not_listed') buildings *= 1.3;
  if (is(home.has_been_extended)) buildings *= 1.05;
  if (Number(home.storeys) > 6) buildings *= 1.05;

  // History
  if (is(home.ever_flooded)) { buildings *= 1.5; contents *= 1.3; }
  if (is(home.has_subsidence)) {
    buildings *= is(home.underpinned) ? 1.6 : 2.4;
    if (!is(home.underpinned)) referrals.push('The home has had subsidence, heave or landslip and has not been underpinned.');
  }

  // Security
  let security = 1;
  if (is(home.window_locks_fitted)) security -= 0.04;
  if (is(home.smoke_alarms_fitted)) security -= 0.02;
  if (/5-lever|deadlock|multi/i.test(home.door_lock_id ?? '')) security -= 0.04;
  if (home.alarm_type_id && !/^no /i.test(home.alarm_type_id)) security -= 0.05;
  contents *= security;
  if (home.flat_floor !== undefined && Number(home.flat_floor) === 0) contents *= 1.05;

  // Use of the home
  const occupancyLoad = { owner_occupied: 1, second_home: 1.2, let_to_tenants: 1.35, holiday_let: 1.5 }[cover.occupancy] ?? 1;
  buildings *= occupancyLoad; contents *= occupancyLoad;
  if (cover.unoccupied === '30_to_60') { buildings *= 1.1; contents *= 1.1; }
  if (cover.unoccupied === 'over_60') referrals.push('The home is left empty for more than 60 days a year.');
  if (cover.lodgers) contents *= 1.08;
  if (cover.workFromHome === 'full') contents *= 1.06;
  if (cover.businessVisitors) { buildings *= 1.04; referrals.push('Clients or customers visit the home.'); }

  // Valuables over the standard single-item limit
  const valuables = Number(payload.valuables?.current_valuation) || 0;
  const valuablesCharge = valuables > 1500 ? valuables * 0.012 : 0;

  // Optional cover
  const adCharge = ({ none: 0, buildings: 0.12, contents: 0.1, both: 0.2 }[cover.accidentalDamage] ?? 0) * (buildings + contents) * 0.5;
  const awayCharge = cover.possessionsAway ? cover.possessionsAway * 0.012 : 0;
  const businessCharge = cover.businessEquipment * 0.015;
  const legalCharge = cover.legalExpenses ? 28 : 0;
  const emergencyCharge = cover.homeEmergency ? 54 : 0;

  // Claims and history
  const claimsLoad = [1, 1.18, 1.45, 1.8][Math.min(cover.claims, 3)];
  const ncdDiscount = 1 - Math.min(cover.ncdYears, 5) * 0.04;
  const excessDiscount = { 100: 1, 250: 0.96, 500: 0.91, 1000: 0.84 }[cover.excess] ?? 1;
  if (cover.refused) referrals.push('An insurer has refused, cancelled or applied special terms to a previous policy.');
  if (cover.ccj) referrals.push('A county court judgment, bankruptcy or IVA in the last 6 years.');
  if (cover.convictions) referrals.push('An unspent criminal conviction.');
  if (cover.claims >= 3) referrals.push('Three or more claims in the last 5 years.');
  if (String(home.ownership_status) === 'renting' && cover.buildingsSum) {
    referrals.push('Buildings cover was requested for a rented home.');
  }

  const rated = (buildings + contents) * claimsLoad * ncdDiscount * excessDiscount;
  const subtotal = rated + valuablesCharge + adCharge + awayCharge + businessCharge;
  const ipt = (subtotal + legalCharge + emergencyCharge) * 0.12;
  const annual = subtotal + legalCharge + emergencyCharge + ipt;

  if (cover.buildingsSum) lines.push(['Buildings cover', buildings * claimsLoad * ncdDiscount * excessDiscount]);
  if (cover.contentsSum) lines.push(['Contents cover', contents * claimsLoad * ncdDiscount * excessDiscount]);
  if (valuablesCharge) lines.push([`High-value items (${gbp(valuables)})`, valuablesCharge]);
  if (adCharge) lines.push(['Accidental damage', adCharge]);
  if (awayCharge) lines.push([`Possessions away from home (up to ${gbp(cover.possessionsAway)})`, awayCharge]);
  if (businessCharge) lines.push([`Business equipment (${gbp(cover.businessEquipment)})`, businessCharge]);
  if (legalCharge) lines.push(['Legal expenses', legalCharge]);
  if (emergencyCharge) lines.push(['Home emergency', emergencyCharge]);
  lines.push(['Insurance premium tax (12%)', ipt]);

  const round = (n) => Math.round(n * 100) / 100;
  return {
    annual: round(annual),
    monthly: round((annual * 1.08) / 12),
    lines: lines.map(([name, amount]) => [name, round(amount)]),
    referrals,
  };
}

function randomQuoteRef() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = 'ADI-';
  for (let i = 0; i < 8; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

$('application-form').addEventListener('submit', (e) => {
  e.preventDefault();
  if ($('get-quote-btn').disabled) return;
  const payload = buildPayload();
  const cover = buildCover();
  const quote = {
    path,
    payload,
    kyc_verified: path === 'squiryl' && sharedKycVerified,
    coverDetails: cover,
    quoteRef: randomQuoteRef(),
    pricing: priceQuote(cover, payload),
    generatedAt: new Date().toISOString(),
  };
  sessionStorage.setItem('ridgeway_quote', JSON.stringify(quote));
  window.location.href = '/confirmation.html';
});
